<template>
  <div class="source-files">
    <div ref="files"  class="files-wrap">
      <div ref="dropzone" v-if="!files.length" class="drop-zone" :class="{processing}">
        <div v-if="waitingDialog || processing" class="processing-info">
          <span class="px-spinner"/>
          <div class="desc">{{waitingDialog ? 'Waiting for' : `Processing ${numberFilesProcessing}`}} Files...</div>
        </div>
        <div v-else class="empty">
          <div class="actions">
            <h3>Drop source files or a directory here!</h3>
            <div class="buttons">
              <span class="clickable" @click="openFileDialog">or Select Files</span>/
              <span class="clickable" @click="openDirectoryDialog">or a Directory</span>
            </div>
          </div>
        </div>
        <input
          ref="input"
          type="file"
          style="display:none"
          multiple
          directory
          allowdirs
          accept="*"
          @change="addFilesFromOpenDialog">
      </div>
      <div v-if="!files.length && !processing" class="desc">
        <span>Static HTML or a single Video, Audio or Image file</span>
      </div>
      <div v-if="files.length" class="files">
        <div class="index-path">
          <fit-in-string v-if="indexPath || indexFile">
            {{pathString}}
          </fit-in-string>
          <div v-if="indexFileError" class="error">{{indexFileError}}</div>
        </div>
        <div class="info">
          <div>
            {{numFiles}} file{{numFiles !== 1?'s':''}}
            <span class="total-size">{{formatBytes(totalSize)}}</span>
          </div>
          <button v-if="numFiles > 1" class="filter secondary compact" @click="includeHiddenFiles=!includeHiddenFiles">
            <span>{{includeHiddenFiles? 'Exclude' : 'Include'}} hidden files</span>
          </button>
        </div>
        <tree v-if="numFiles > 1" :value="folders" class="file-tree" />
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-webpack-loader-syntax
import * as FolderReaderWorker from 'worker-loader!../../../../lib/folder-reader'
import { readDataTransferDirectories } from '../../../../lib/data-transfer'
import smoothHeight from 'vue-smooth-height'
import Tree from '../../../elements/UI/TreeView/Tree'
import { bytesToString } from '../../../../lib/storage'
import { AWSource } from '../../../../models/AWSource'
import FitInString from '../../../elements/UI/FitInString'

export default {
  name: 'SourceFiles',
  mixins: [ smoothHeight ],
  components: { Tree, FitInString },
  props: {
    value: { type: Array, default: () => ([]) },
    indexPath: { type: String, default: '' }
  },
  data: () => ({
    rawFiles: [],
    dragAndDropCapable: false,
    uploadPercentage: 0,
    includeHiddenFiles: false,
    waitingDialog: false,
    processing: false,
    numberFilesProcessing: 0,
    processingTimeout: null,
    folderReaderWorker: null,
    indexFile: null,
    indexFileError: null
  }),

  computed: {
    pathString () {
      return this.indexPath || this.indexFile ? this.indexPath || this.indexFile.path || this.indexFile.file.name : ''
    },

    pathStringParts () {
      const str = this.pathString
      const last = str.substring(str.length - 14)
      const first = str.replace(last, '')
      return [first, last]
    },

    files: {
      get () {
        return this.value
      },
      set (files) {
        this.rawFiles = files
        try {
          this.indexFile = this.getIndexFile(files)
          this.indexFileError = null
        } catch (e) {
          this.indexFile = null
          this.indexFileError = e.message
          console.error(`%c files.set() %c indexFileError: `, 'background:#ff0000;color:#fff', 'color:#ff0000', this.indexFileError)
        }
        const indexUrl = this.indexFile ? this.indexFile.path || this.indexFile.file.name : ''
        if (!this.includeHiddenFiles) {
          files = files.filter(file => !/^\.|\/\./.test(file.path))
        }
        this.$emit('input', { files, indexUrl })
      }
    },

    folders () {
      const folders = []
      this.files.forEach(item => {
        let pathItems = item.path.split('/')
        pathItems.pop()
        let current = folders
        while (pathItems.length) {
          const [name, ...tail] = pathItems
          pathItems = tail
          if (!current.find(x => x.name === name)) {
            current.push({ name, children: [] })
          }
          current = current.find(x => x.name === name).children
        }
        current.push({
          name: item.file.name,
          path: item.path,
          size: item.file.size
        })
      })
      const setSize = item => {
        if (item.children) {
          const size = item.children.reduce((res, ch) => res + setSize(ch), 0)
          item.size = size
          return size
        } else {
          return item.size
        }
      }
      const sortDip = items => {
        items = items.sort((a, b) => {
          if (a.children && !b.children) { return -1 }
          if (!a.children && b.children) { return 1 }
          if (a.name < b.name) { return -1 }
          if (a.name > b.name) { return 1 }
          return 0
        })
        items.forEach(item => {
          if (item.children) {
            item.children = sortDip(item.children)
          }
        })
        return items
      }
      folders.forEach(item => {
        if (item.children) {
          item.size = setSize(item)
        }
      })
      return sortDip(folders)
    },

    totalSize () {
      return this.folders.reduce((res, x) => res + x.size, 0)
    },

    numFiles () {
      return this.files.length
    }
  },

  watch: {
    includeHiddenFiles () {
      this.files = this.rawFiles
    },

    files (newValue) {
      if (!newValue || !newValue.length) {
        this.$nextTick(this.setupDropZone)
      }
    }
  },

  mounted () {
    this.dragAndDropCapable = this.determineDragAndDropCapable()
    this.setupDropZone()
    this.$smoothElement({
      el: this.$refs.files,
      transition: 'height .15s'
    })
  },

  destroyed () {
    if (this.folderReaderWorker) {
      this.folderReaderWorker.terminate()
    }
  },

  methods: {
    setupDropZone () {
      if (this.dragAndDropCapable && this.$refs.dropzone) {
        ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(eName => {
          this.$refs.dropzone.addEventListener(eName, e => {
            e.preventDefault()
            e.stopPropagation()
          }, false)
        })
        this.$refs.dropzone.addEventListener('drop', this.addDroppedFiles)
      }
    },

    clearFiles () {
      this.files = []
    },

    getSize (item) {
      if (item.children) {
        return item.children.reduce((res, ch) => res + this.getSize(ch), 0)
      } else {
        return item.size
      }
    },

    formatBytes (bytes) {
      return bytesToString(bytes)
    },

    openFileDialog () {
      if (this.$refs.input) {
        this.$refs.input.removeAttribute('webkitdirectory')
        this.$refs.input.click()
      }
    },

    openDirectoryDialog () {
      if (this.$refs.input) {
        this.$refs.input.setAttribute('webkitdirectory', 'webkitdirectory')
        this.$nextTick(() => { this.$refs.input.click() })
      }
    },

    async addDroppedFiles (event) {
      this.processing = true
      event.preventDefault()
      this.processing = true
      if (!this.folderReaderWorker) {
        this.setupReaderWorker()
      }
      readDataTransferDirectories(event.dataTransfer, files => {
        if (!files) return
        this.folderReaderWorker.postMessage({ type: 'fromInput', files })
        this.processingTimeout = setTimeout(() => {
          this.processing = true
          this.numberFilesProcessing = files.length
        }, 80)
      })
    },

    async addFilesFromOpenDialog (e) {
      if (!this.folderReaderWorker) {
        this.setupReaderWorker()
      }
      // map files before sending to the worker in order to preserve webkitRelativePath in Safari
      const files = Array.from(e.target.files).map(file => ({ file, path: file.webkitRelativePath }))
      this.folderReaderWorker.postMessage({ type: 'fromInput', files })

      this.processingTimeout = setTimeout(() => {
        this.processing = true
        this.numberFilesProcessing = e.target.files.length
      }, 80)
    },

    setupReaderWorker () {
      this.folderReaderWorker = new FolderReaderWorker()
      this.folderReaderWorker.onmessage = event => {
        const data = event.data
        if (data.sharedRootFolder) {
          this.$emit('foundSharedRootFolder', data.sharedRootFolder)
        }
        clearTimeout(this.processingTimeout)
        this.processing = false
        this.numberFilesProcessing = 0
        this.files = data.files || []
      }
      this.folderReaderWorker.onerror = event => {
        console.error(`%c Worker error %c : `, 'background:#ffbb00;color:#000', 'color:#00aaff', event)
      }
    },

    getIndexFile (files) {
      if (!files) files = this.files
      switch (files.length) {
        case 0:
          throw new Error('No source files')
        case 1:
          AWSource.validSourceTypeFromIndexFile(files[0].file)
          return files[0]
        default:
          const index = files.find(file => file.path.toLowerCase() === 'index.html')
          if (index) return index
          throw new Error(`index.html Not Found`)
      }
    },

    determineDragAndDropCapable () {
      const div = document.createElement('div')
      return (('draggable' in div) ||
        ('ondragstart' in div && 'ondrop' in div)) &&
        'FormData' in window &&
        'FileReader' in window
    }
  }
}
</script>

<style lang="scss">
  @import "../../../../scss/vars";

  .fit-in-string{
    display: inline-flex;
    white-space: nowrap;
    max-width: 100%;
    .first {
      flex-grow: 1;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .last {
      flex-shrink: 0;
    }
  }

  .source-files {
    .files-wrap {
      //margin: $small-padding 0;
      //background: $px-color-grey-semi;
    }
    .desc {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 0;
      padding: $basic-padding;
    }
    .drop-zone {
      display: flex;
      flex-flow: wrap;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: $basic-size * 2;
      text-align: center;
      background: $bg-semi-transparent-color;
      //border: 1px $px-color-grey-semi dashed;
      /*border-width: 0 1px 1px;*/

      &.processing {
        /*border-top-width: 1px;*/
      }
      .px-spinner {
        display: inline-block;
      }

      .empty {
        width: 100%;

        .actions {
          display: flex;
          flex-flow: column;
          align-items: center;
          padding: $three-quarters $basic-padding;

          & > * {
            min-height: $basic-size / 2;
            margin: 0;
          }

          h3 {
            margin-top: $small-padding;
          }

          .buttons {
            display: flex;
            padding: $small-padding 0;
            color: transparentize($px-color-blue, 0.7);

            .clickable {
              display: block;
              font-size: $font-size-caption;
              cursor: pointer;
              color: $px-color-blue;
              padding: 0 $small-padding / 2;

              &:active {
                background: transparentize($px-color-grey-semi, 0.2);
                color: $px-color-blue-dark;
              }
            }
          }
        }
      }
    }

    .files {
      background: #fff;
      .info {
        display: flex;
        align-items: center;
        min-height: $basic-size;
        padding-left: $basic-padding;

        .filter {
          margin-left: auto;
          font-size: $font-size-micro;
          color: $px-color-blue;
          //width: $basic-size * 1.5;
          text-align: center;
        }
      }
      .file-tree {
        font-size: $font-size-caption;
        padding: 0 $basic-padding $basic-padding;
      }

      .index-path {
        display: flex;
        align-items: center;
        padding: $basic-padding;
        font-size: $h3-font-size;
        height: $basic-size;
        background: #fff;
        color: $px-color-blue-dark;
      }
    }
    .progress {
      width: 400px;
      margin: auto;
      display: block;
      margin-top: 20px;
      margin-bottom: 20px;
    }
  }
</style>
